import { useState, useEffect } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import {
  Layout,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  AutoComplete,
  Pagination,
  Modal,
  message,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Avatar,
  Dropdown,
  Select
} from 'antd';
import {
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
  AudioOutlined,
  RightOutlined,
  CloudUploadOutlined,
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  FilterOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import './App.css';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

interface Recording {
  id: number;
  name: string;
  size: number;
  modified: string;
  path: string;
  start_time?: string;
  end_time?: string;
  duration_seconds?: number;
  meeting?: string;
  transcription_status?: string;
  transcription_path?: string;
  drive_file_id?: string;
  drive_web_link?: string;
  transcribed_at?: string;
  uploading?: boolean;
}

function App() {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [meetings, setMeetings] = useState<string[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState<Recording | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{email: string; name: string} | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [needsOAuthForDrive, setNeedsOAuthForDrive] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const recordsPerPage = 10;

  useEffect(() => {
    // Check authentication status and load data
    checkAuthStatus();
  }, []);

  useEffect(() => {
    // Load recordings when authenticated
    if (isAuthenticated) {
      fetchRecordings();
      fetchMeetings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    // Set up automatic refresh for recordings to show real-time transcription status updates
    if (isAuthenticated) {
      const interval = setInterval(() => {
        // Only refresh if not currently loading
        if (!recordingsLoading) {
          fetchRecordings(false); // false = don't show loading spinner for background refresh
        }
      }, 10000); // Refresh every 10 seconds

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, recordingsLoading, statusFilter]);

  useEffect(() => {
    // Refetch recordings when status filter changes
    if (isAuthenticated) {
      setCurrentPage(1); // Reset to first page when filter changes
      fetchRecordings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated || false);
      if (data.authenticated) {
        setUserProfile({
          email: data.user_email || '',
          name: data.user_name || ''
        });
        setNeedsReauth(data.needs_reauth || false);
      } else {
        setUserProfile(null);
        setNeedsReauth(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      setUserProfile(null);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: CredentialResponse) => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      // Send the Google ID token to our backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: credentialResponse.credential,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsAuthenticated(true);
        setUserProfile({
          email: data.user?.email || '',
          name: data.user?.name || ''
        });
        setNeedsReauth(false);
        setNeedsOAuthForDrive(data.needs_oauth_for_drive || false);
        message.success('Successfully signed in with Google!');
      } else {
        setAuthError(data.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Error during Google login:', error);
      setAuthError('Failed to authenticate with Google');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setAuthError('Google login failed. Please try again.');
    setAuthLoading(false);
  };

  const fetchRecordings = async (showLoading = true, page = currentPage) => {
    if (showLoading) {
      setRecordingsLoading(true);
    }
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: recordsPerPage.toString()
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/recordings?${params.toString()}`;
      console.log('Fetching recordings from:', url);
      const response = await fetch(url);
      const data = await response.json();
      console.log('Recordings response:', { status: response.status, data });

      if (response.ok) {
        const recordings = data.recordings || [];
        console.log('Setting recordings:', recordings);
        setRecordings(recordings);
        setTotalPages(data.total_pages || 1);
        setTotalCount(data.total_count || 0);
        setCurrentPage(data.page || 1);
      } else {
        console.error('Failed to fetch recordings:', data.error);
        setRecordings([]);
        setTotalPages(1);
        setTotalCount(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching recordings:', error);
      setRecordings([]);
      setTotalPages(1);
      setTotalCount(0);
      setCurrentPage(1);
    } finally {
      setRecordingsLoading(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings`);
      const data = await response.json();

      if (response.ok) {
        setMeetings(data.meetings || []);
      } else {
        console.error('Failed to fetch meetings:', data.error);
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setMeetings([]);
    }
  };

  const updateRecordingMeeting = async (recordingId: number, meeting: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/${recordingId}/meeting`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ meeting })
      });

      if (response.ok) {
        // Fetch fresh recording data to get updated transcription paths
        await fetchRecordings(false); // false = don't show loading spinner

        // Refresh meetings list to include new meeting if it's new
        if (meeting && !meetings.includes(meeting)) {
          fetchMeetings();
        }
      } else {
        console.error('Failed to update meeting');
      }
    } catch (error) {
      console.error('Error updating meeting:', error);
    }
  };

  const startTranscription = async (recordingId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/${recordingId}/transcribe`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state to show transcription queued
        setRecordings(prev => prev.map(recording =>
          recording.id === recordingId
            ? { ...recording, transcription_status: 'queued' }
            : recording
        ));
        message.success('Transcription started');
      } else {
        message.error(data.error || 'Failed to start transcription');
      }
    } catch (error) {
      console.error('Error starting transcription:', error);
      message.error('Failed to start transcription');
    }
  };

  const handleDeleteClick = (recording: Recording) => {
    setRecordingToDelete(recording);
    setDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setRecordingToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!recordingToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/${recordingToDelete.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        // Remove the recording from local state
        setRecordings(prev => prev.filter(r => r.id !== recordingToDelete.id));
        setDeleteModalVisible(false);
        setRecordingToDelete(null);
        message.success('Recording deleted successfully');
      } else {
        message.error(data.error || 'Failed to delete recording');
      }
    } catch (error) {
      console.error('Error deleting recording:', error);
      message.error('Failed to delete recording');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetTranscriptionStatus = async (recordingId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/${recordingId}/reset-transcription`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state to reset transcription status
        setRecordings(prev => prev.map(recording =>
          recording.id === recordingId
            ? {
                ...recording,
                transcription_status: 'not_started',
                transcription_path: undefined,
                drive_file_id: undefined,
                drive_web_link: undefined
              }
            : recording
        ));
        message.success('Transcription status reset successfully');
      } else {
        message.error(data.error || 'Failed to reset transcription status');
      }
    } catch (error) {
      console.error('Error resetting transcription status:', error);
      message.error('Failed to reset transcription status');
    }
  };

  const initiateOAuthForDrive = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/authorize`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (response.ok) {
        // Open OAuth popup
        const popup = window.open(
          data.authorization_url,
          'google-oauth',
          'width=600,height=600,scrollbars=yes,resizable=yes'
        );
        
        // Listen for OAuth completion
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== process.env.REACT_APP_BACKEND_URL) return;
          
          if (event.data.type === 'oauth_success') {
            popup?.close();
            window.removeEventListener('message', handleMessage);
            message.success('Google Drive access granted!');
            setNeedsOAuthForDrive(false);
            // Refresh auth status
            checkAuthStatus();
          } else if (event.data.type === 'oauth_error') {
            popup?.close();
            window.removeEventListener('message', handleMessage);
            message.error('Failed to grant Google Drive access');
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Check if popup was closed without completion
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
          }
        }, 1000);
      } else {
        message.error('Failed to initiate Google Drive authorization');
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      message.error('Failed to initiate Google Drive authorization');
    }
  };

  const uploadToGoogleDrive = async (recordingId: number) => {
    try {
      // Check if we need OAuth for Drive first
      if (needsOAuthForDrive) {
        message.info('Please grant Google Drive access first');
        initiateOAuthForDrive();
        return;
      }

      // Set uploading state
      setRecordings(prev => prev.map(recording =>
        recording.id === recordingId
          ? { ...recording, uploading: true }
          : recording
      ));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/${recordingId}/upload-to-drive`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state with Google Drive link
        setRecordings(prev => prev.map(recording =>
          recording.id === recordingId
            ? {
                ...recording,
                drive_web_link: data.drive_link,
                uploading: false
              }
            : recording
        ));
        message.success('Successfully uploaded to Google Drive!');
      } else {
        setRecordings(prev => prev.map(recording =>
          recording.id === recordingId
            ? { ...recording, uploading: false }
            : recording
        ));
        
        // Check if it's an auth error - trigger OAuth flow
        if (response.status === 401 || data.error?.includes('credentials') || data.error?.includes('refresh_token')) {
          message.info('Google Drive access required. Please authorize access.');
          setNeedsOAuthForDrive(true);
          initiateOAuthForDrive();
        } else {
          message.error(data.error || 'Failed to upload to Google Drive');
        }
      }
    } catch (error) {
      console.error('Error uploading to Google Drive:', error);
      setRecordings(prev => prev.map(recording =>
        recording.id === recordingId
          ? { ...recording, uploading: false }
          : recording
      ));
      message.error('Failed to upload to Google Drive');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchRecordings(true, page);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setIsAuthenticated(false);
        setUserProfile(null);
        setRecordings([]);
        setMeetings([]);
        message.success('Successfully logged out');
      } else {
        message.error('Failed to logout');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Force logout on client side even if server request fails
      setIsAuthenticated(false);
      setUserProfile(null);
      setRecordings([]);
      setMeetings([]);
      message.info('Logged out locally');
    }
  };

  const redoAllTranscripts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/redo-all-transcripts`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        message.success(`Started retranscribing ${data.count} recordings`);
        // Refresh the recordings list to show updated statuses
        fetchRecordings();
      } else {
        message.error(data.error || 'Failed to start bulk transcription');
      }
    } catch (error) {
      console.error('Error starting bulk transcription:', error);
      message.error('Failed to start bulk transcription');
    }
  };

  const uploadAllToGoogleDocs = async () => {
    try {
      // Check if we need OAuth for Drive first
      if (needsOAuthForDrive) {
        message.info('Please grant Google Drive access first');
        initiateOAuthForDrive();
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/upload-all-to-drive`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        message.success(`Started uploading ${data.count} transcriptions to Google Drive`);
        // Refresh the recordings list to show updated statuses
        fetchRecordings();
      } else {
        // Check if it's an auth error - trigger OAuth flow
        if (response.status === 401 || data.error?.includes('credentials') || data.error?.includes('refresh_token')) {
          message.info('Google Drive access required. Please authorize access.');
          setNeedsOAuthForDrive(true);
          initiateOAuthForDrive();
        } else {
          message.error(data.error || 'Failed to start bulk upload');
        }
      }
    } catch (error) {
      console.error('Error starting bulk upload:', error);
      message.error('Failed to start bulk upload');
    }
  };

  const clearAllDriveLinks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recordings/redo-all-drive-uploads`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        message.success(`Cleared Drive links for ${data.count} transcriptions`);
        // Refresh the recordings list to show updated statuses
        fetchRecordings();
      } else {
        message.error(data.error || 'Failed to clear Drive links');
      }
    } catch (error) {
      console.error('Error clearing Drive links:', error);
      message.error('Failed to clear Drive links');
    }
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const renderTranscriptionStatus = (record: Recording) => {
    const status = record.transcription_status || 'not_started';

    switch (status) {
      case 'not_started':
        return (
          <Button
            type="primary"
            icon={<AudioOutlined />}
            onClick={() => startTranscription(record.id)}
            size="small"
          >
            Transcribe
          </Button>
        );

      case 'queued':
        return (
          <Space>
            <Spin size="small" />
            <Text>Queued...</Text>
            <Button
              size="small"
              danger
              onClick={() => resetTranscriptionStatus(record.id)}
            >
              Reset
            </Button>
          </Space>
        );

      case 'in_progress':
        return (
          <Space>
            <Spin size="small" />
            <Text>Transcribing...</Text>
            <Button
              size="small"
              danger
              onClick={() => resetTranscriptionStatus(record.id)}
            >
              Reset
            </Button>
          </Space>
        );

      case 'completed':
        return (
          <Space>
            <Tag color="rgb(131,203,206)">Completed</Tag>

            {record.drive_web_link ? (
              // Google Drive link available
              <Tooltip title="View Google Doc">
                <Button
                  type="link"
                  icon={<ExportOutlined />}
                  href={record.drive_web_link}
                  target="_blank"
                  size="small"
                />
              </Tooltip>
            ) : (
              // No Google Drive link - show upload button and local download
              <Space>
                {record.uploading ? (
                  <Space>
                    <Spin size="small" />
                    <Text type="secondary" style={{ fontSize: '12px' }}>Uploading...</Text>
                  </Space>
                ) : (
                  <Tooltip title="Upload to Google Drive">
                    <Button
                      type="link"
                      icon={<CloudUploadOutlined />}
                      onClick={() => uploadToGoogleDrive(record.id)}
                      size="small"
                      style={{ color: 'rgb(3,135,159)' }}
                    />
                  </Tooltip>
                )}

                {record.transcription_path && (
                  <Tooltip title="Download Local File">
                    <Button
                      type="link"
                      icon={<ExportOutlined />}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/transcriptions/${record.transcription_path.split('/').pop()}`}
                      target="_blank"
                      size="small"
                      style={{ color: 'rgb(3,135,159)' }}
                    />
                  </Tooltip>
                )}
              </Space>
            )}

            <Tooltip title="Redo Transcription">
              <Button
                type="link"
                icon={<ReloadOutlined />}
                onClick={() => startTranscription(record.id)}
                size="small"
                style={{ color: 'rgb(3,135,159)' }}
              />
            </Tooltip>
          </Space>
        );

      case 'failed':
        return (
          <Space>
            <Tag color="rgb(216,18,90)">Failed</Tag>
            <Button
              size="small"
              onClick={() => startTranscription(record.id)}
            >
              Retry
            </Button>
          </Space>
        );

      default:
        return <Text type="secondary">Unknown status</Text>;
    }
  };

  const renderMeetingCell = (record: Recording) => {
    const options = meetings.map(meeting => ({ value: meeting }));

    return (
      <AutoComplete
        value={record.meeting || undefined}
        options={options}
        onSelect={(value) => updateRecordingMeeting(record.id, value)}
        onBlur={(e) => {
          const value = (e.target as HTMLInputElement).value;
          if (value !== record.meeting) {
            updateRecordingMeeting(record.id, value);
          }
        }}
        placeholder="Select or enter meeting name"
        style={{ width: 200 }}
        filterOption={(inputValue, option) =>
          option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
        }
      />
    );
  };

  const columns: ColumnsType<Recording> = [
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (startTime: string, record: Recording) => (
        <div>
          {startTime && record.end_time ? (
            <>
              <div>{startTime}</div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                to {record.end_time}
              </Text>
            </>
          ) : (
            <Text type="secondary">{record.modified}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration',
      render: (duration: number) => (
        <Text type="secondary">{formatDuration(duration)}</Text>
      ),
    },
    {
      title: 'Meeting',
      dataIndex: 'meeting',
      key: 'meeting',
      render: (_, record) => renderMeetingCell(record),
    },
    {
      title: 'Transcription',
      key: 'transcription',
      render: (_, record) => renderTranscriptionStatus(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Delete recording">
          <Button
            type="link"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            size="small"
            style={{ color: 'rgb(216,18,90)' }}
            danger
          />
        </Tooltip>
      ),
    },
  ];

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Card style={{ width: 400, textAlign: 'center' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Title level={2}>
                <RightOutlined /> River
              </Title>
              <Text type="secondary">
                Please sign in with Google to access your transcriptions
              </Text>

              {authError && (
                <Alert
                  message={authError}
                  type="error"
                  showIcon
                />
              )}

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                  text="continue_with"
                  theme="outline"
                  size="large"
                  width="280"
                />
              </div>
              
              {authLoading && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Spin size="small" />
                  <span style={{ marginLeft: 8 }}>Signing you in...</span>
                </div>
              )}
            </Space>
          </Card>
        </Content>
      </Layout>
    );
  }

  // Create dropdown menu items
  const userMenuItems = [
    {
      key: 'profile',
      label: (
        <Space>
          <UserOutlined />
          <div className="user-profile-info">
            <div className="user-profile-name">{userProfile?.name || 'User'}</div>
            <div className="user-profile-email">{userProfile?.email}</div>
          </div>
        </Space>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: (
        <Space>
          <LogoutOutlined />
          Logout
        </Space>
      ),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ color: 'rgba(255,255,255,0.6)', margin: 0 }}>
              <RightOutlined /> River
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 160 }}
                placeholder="Filter by status"
                suffixIcon={<FilterOutlined />}
              >
                <Select.Option value="all">All Recordings</Select.Option>
                <Select.Option value="not_completed">Not Completed</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="not_started">Not Started</Select.Option>
                <Select.Option value="queued">Queued</Select.Option>
                <Select.Option value="in_progress">In Progress</Select.Option>
                <Select.Option value="failed">Failed</Select.Option>
              </Select>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchRecordings()}
                loading={recordingsLoading}
              >
                Refresh
              </Button>
              
              <Button
                icon={<AudioOutlined />}
                onClick={redoAllTranscripts}
                type="default"
              >
                Redo All Transcripts
              </Button>
              
              <Button
                icon={<CloudUploadOutlined />}
                onClick={uploadAllToGoogleDocs}
                type="default"
              >
                Upload All to Drive
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={clearAllDriveLinks}
                type="default"
                style={{ color: '#4285F4', borderColor: '#4285F4' }}
              >
                Clear All Drive Links
              </Button>
              
              {needsOAuthForDrive && (
                <Button
                  icon={<CloudUploadOutlined />}
                  onClick={initiateOAuthForDrive}
                  type="primary"
                  size="small"
                >
                  Enable Drive Access
                </Button>
              )}
              
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                arrow
                overlayClassName="user-dropdown"
              >
                <Button type="text" style={{ padding: '4px 8px' }}>
                  <Space>
                    <Avatar 
                      size="small" 
                      icon={<UserOutlined />}
                      style={{ backgroundColor: 'rgb(246,218,28)', color: 'rgb(61,60,61)' }}
                    />                    
                    <DownOutlined style={{ fontSize: '10px' }} />
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </Header>

      <Content style={{ padding: '24px' }}>
        {needsReauth && (
          <Alert
            message="Google Credentials Need Refresh"
            description="Your Google Drive access has expired. Please logout and login again to upload transcriptions to Google Drive."
            type="warning"
            showIcon
            closable
            onClose={() => setNeedsReauth(false)}
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={handleLogout}>
                Logout & Re-login
              </Button>
            }
          />
        )}
        
        {needsOAuthForDrive && (
          <Alert
            message="Google Drive Access Required"
            description="To upload transcriptions to Google Drive, you need to grant additional permissions."
            type="info"
            showIcon
            closable
            onClose={() => setNeedsOAuthForDrive(false)}
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={initiateOAuthForDrive}>
                Enable Drive Access
              </Button>
            }
          />
        )}
        
        {recordingsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading recordings...</div>
          </div>
        ) : recordings.length > 0 ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Table
              columns={columns}
              dataSource={recordings}
              rowKey="id"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />

            <Row justify="space-between" align="middle">
              <Col>
                <Text type="secondary">
                  Showing {recordings.length} of {totalCount} 
                </Text>
              </Col>
              <Col>
                <Pagination
                  current={currentPage}
                  total={totalCount}
                  pageSize={recordsPerPage}
                  showSizeChanger={false}
                  showQuickJumper
                  onChange={handlePageChange}
                />
              </Col>
            </Row>
          </Space>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text type="secondary">No recordings found on the server.</Text>
          </div>
        )}
      </Content>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Recording"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteLoading}
        okText="Delete"
        okType="danger"
      >
        <Space direction="vertical">
          <Text>Are you sure you want to delete "{recordingToDelete?.name}"?</Text>
          <Text type="secondary">This will permanently:</Text>
          <ul>
            <li>Remove the recording from the database</li>
            <li>Delete the original .m4a audio file from Voice Memos</li>
            <li>Delete any associated transcription files</li>
          </ul>
          <Text strong>This action cannot be undone.</Text>
        </Space>
      </Modal>
    </Layout>
  );
}

export default App;